/**
 * Entry point. Wires the UI to the Pipeline.
 *
 * The dropdowns are auto-generated from COMPONENTS. As the user picks
 * variants, we update the total estimated download size and re-instantiate
 * sessions on demand. The result is a .splat ArrayBuffer that's piped into the
 * mkkellogg viewer.
 */
import { COMPONENTS, modelUrl, type Variant } from "./components";
import { preprocessImage, preprocessImageRaw } from "./preprocess";
import { loadComponent, type ComponentId } from "./models";
import { Pipeline } from "./pipeline";
import { setSplat } from "./viewer";
import { prefetch } from "./model-cache";

const $ = <T extends Element = HTMLElement>(s: string) => document.querySelector(s) as T;
const log = (msg: string) => {
  const el = $("#log") as HTMLPreElement;
  el.textContent += msg + "\n";
  el.scrollTop = el.scrollHeight;
  console.log(msg);
};

const selection: Record<ComponentId, Variant> = {} as any;

interface SamplingSpec {
  key: "steps" | "guidanceScale" | "shift" | "numGaussians";
  name: string;
  min: number;
  max: number;
  step: number;
  default: number;
  format?: (v: number) => string;
}
const SAMPLING_SPECS: SamplingSpec[] = [
  { key: "steps",         name: "Steps",     min: 4, max: 30, step: 1,   default: 20 },
  { key: "guidanceScale", name: "CFG",       min: 1, max: 7,  step: 0.5, default: 3.0 },
  { key: "shift",         name: "Shift",     min: 1, max: 5,  step: 0.5, default: 3.0 },
  { key: "numGaussians",  name: "Gaussians", min: 32768, max: 262144, step: 32768, default: 32768,
    format: (v) => `${(v / 1024).toFixed(0)}k` },
];
const sampling: Record<SamplingSpec["key"], number> = Object.fromEntries(
  SAMPLING_SPECS.map(s => [s.key, s.default])) as any;

interface PrepSpec {
  key: "erodeRadius" | "cropMargin" | "bboxThreshold";
  name: string;
  min: number; max: number; step: number; default: number;
  format?: (v: number) => string;
}
const PREP_SPECS: PrepSpec[] = [
  { key: "erodeRadius",   name: "Erode",     min: 0, max: 5,   step: 1,    default: 1 },
  { key: "cropMargin",    name: "Margin",    min: 1.0, max: 1.6, step: 0.05, default: 1.2,
    format: (v) => v.toFixed(2) + "x" },
  { key: "bboxThreshold", name: "Threshold", min: 0, max: 64,  step: 1,    default: 8 },
];
const prepCfg: Record<PrepSpec["key"], number> = Object.fromEntries(
  PREP_SPECS.map(s => [s.key, s.default])) as any;

/** Runtime kernel toggles.
 *  - flash:  un-chunked flow ONNX, lets ORT-Web fuse SDPA into the
 *            WebGPU MultiHeadAttention kernel.
 *  - gpuIo:  WebGPU IO binding for the flow sampling loop. Skip CPU<->GPU
 *            round-trips on the latent / camera between steps and run the
 *            Euler+CFG update as a WGSL kernel. Both default on; disable
 *            via the UI checkbox or `?gpuio=0` / `?flash=0` in the URL. */
const urlParams = new URLSearchParams(location.search);
const vanilla = urlParams.get("vanilla") === "1";
// `?raw=1` skips RMBG / crop / composite -- use this with the
// /tmp/prep_python.png that python_debug.py writes so the web encoders see
// exactly the same 1024x1024 RGB as the python reference.
const rawPrep = urlParams.get("raw") === "1";
// `?ref=<base-url>` loads python's seeded noise (saved by python_debug.py's
// save_ref) so the flow loop starts from the same point. Combined with
// `?raw=1` + `?vanilla=1` this gives us the most faithful match possible
// short of replacing RMBG-1.4 with BiRefNet.
const refBase = urlParams.get("ref") || "";
// `?skipflow=1` bypasses the flow sampler entirely and uses python's saved
// flow_latent. Lets us test octree + GS decoder + unpack matching even
// when the flow path itself is unrunnable (no flow ONNX deployed, or just
// to skip the cost). Requires `?ref=URL` to know where to load from.
const skipFlow = urlParams.get("skipflow") === "1";
// `?opboost=N` (default 0) adds N to the opacity feature before sigmoid in
// the unpacker. Only useful when running a quantized gs_decoder whose
// out_proj weights took an fp16 precision hit; the fp32 gs_decoder doesn't
// need it. Defaults off now that fp32 is the recommended variant.
const opacityBoost = parseFloat(urlParams.get("opboost") ?? "0");
const kernels = {
  // Flash attention is opt-in (`?flash=1`) -- the un-chunked
  // `flow_*_flash.onnx` variant isn't deployed and the chunked baseline
  // is what the prod pipeline actually uses.
  flowFlash: !vanilla && urlParams.get("flash") === "1",
  flowGpuIo: !vanilla && urlParams.get("gpuio") !== "0",
  vanilla,
};

/** HEAD-probe each declared variant for a component; return the ones whose
 *  ONNX file actually exists on disk, plus the total transfer size for each
 *  (graph + .data sidecar). Sizes feed the dropdown labels and the
 *  total-download footer. */
type ProbedVariant = { v: Variant; sizeMb: number | undefined };
async function availableVariants(c: typeof COMPONENTS[number]): Promise<ProbedVariant[]> {
  const probes = await Promise.all(c.variants.map(async (v) => {
    try {
      const onnxResp = await fetch(modelUrl(c.id, v), { method: "HEAD" });
      if (!onnxResp.ok) return null;
      const ct = onnxResp.headers.get("Content-Type") || "";
      if (ct.startsWith("text/html")) return null;
      let bytes = Number(onnxResp.headers.get("Content-Length") || "0");
      const dataResp = await fetch(modelUrl(c.id, v) + ".data", { method: "HEAD" });
      if (dataResp.ok) bytes += Number(dataResp.headers.get("Content-Length") || "0");
      return { v, sizeMb: bytes ? Math.round(bytes / 1_000_000) : undefined };
    } catch {
      return null;
    }
  }));
  return probes.filter((p): p is ProbedVariant => p !== null);
}

const missingComponents: ComponentId[] = [];

async function renderConfig() {
  const grid = $("#quant-grid")!;
  grid.innerHTML = "";
  missingComponents.length = 0;
  for (const c of COMPONENTS) {
    const div = document.createElement("div");
    div.className = "component";
    div.innerHTML = `
      <div class="name">${c.name}</div>
      <select data-id="${c.id}"></select>
      <div class="size" id="size-${c.id}"></div>
    `;
    const sel = div.querySelector("select") as HTMLSelectElement;
    const available = await availableVariants(c);

    if (available.length === 0) {
      div.classList.add("missing");
      const opt = document.createElement("option");
      opt.value = ""; opt.textContent = "NOT FOUND";
      opt.disabled = true; sel.appendChild(opt);
      sel.disabled = true;
      missingComponents.push(c.id as ComponentId);
    } else {
      for (const { v, sizeMb } of available) {
        const opt = document.createElement("option");
        opt.value = v;
        opt.textContent = sizeMb != null ? `${v} (${sizeMb} MB)` : v;
        sel.appendChild(opt);
        variantSize[`${c.id}/${v}`] = sizeMb ?? 0;
      }
      const variantNames = available.map(p => p.v);
      const pick = variantNames.includes(c.default) ? c.default : variantNames[0];
      sel.value = pick;
      selection[c.id as ComponentId] = pick;
      sel.addEventListener("change", () => {
        selection[c.id as ComponentId] = sel.value as Variant;
        updateTotal();
      });
    }
    grid.appendChild(div);
  }
  updateTotal();
  renderMissingBanner();
}

/** Probed sizes keyed by `<componentId>/<variant>`. Populated by
 *  availableVariants as the page loads; consumed by updateTotal. */
const variantSize: Record<string, number> = {};

function renderSliders() {
  const host = $("#sliders")!;
  host.innerHTML = "";
  for (const s of SAMPLING_SPECS) {
    const div = document.createElement("div");
    div.className = "slider";
    const fmt = s.format ?? ((v: number) => v.toString());
    div.innerHTML = `
      <div class="row">
        <span class="name">${s.name}</span>
        <span class="value" id="val-${s.key}">${fmt(sampling[s.key])}</span>
      </div>
      <input type="range" data-k="${s.key}"
             min="${s.min}" max="${s.max}" step="${s.step}" value="${sampling[s.key]}">
    `;
    const input = div.querySelector("input") as HTMLInputElement;
    input.addEventListener("input", () => {
      const v = Number(input.value);
      sampling[s.key] = v;
      const valEl = document.getElementById(`val-${s.key}`);
      if (valEl) valEl.textContent = fmt(v);
    });
    host.appendChild(div);
  }
}

function renderPrepSliders() {
  const host = $("#prep-sliders")!;
  host.innerHTML = "";
  for (const s of PREP_SPECS) {
    const div = document.createElement("div");
    div.className = "slider";
    const fmt = s.format ?? ((v: number) => v.toString());
    div.innerHTML = `
      <div class="row">
        <span class="name">${s.name}</span>
        <span class="value" id="val-prep-${s.key}">${fmt(prepCfg[s.key])}</span>
      </div>
      <input type="range" data-k="${s.key}"
             min="${s.min}" max="${s.max}" step="${s.step}" value="${prepCfg[s.key]}">
    `;
    const input = div.querySelector("input") as HTMLInputElement;
    input.addEventListener("input", () => {
      const v = Number(input.value);
      prepCfg[s.key] = v;
      const valEl = document.getElementById(`val-prep-${s.key}`);
      if (valEl) valEl.textContent = fmt(v);
    });
    host.appendChild(div);
  }
}

function renderMissingBanner() {
  const id = "missing-banner";
  document.getElementById(id)?.remove();
  if (missingComponents.length === 0) return;

  const banner = document.createElement("div");
  banner.id = id;
  banner.className = "missing-banner";
  const names = missingComponents.map(id => COMPONENTS.find(c => c.id === id)!.name);
  const exportScript: Record<ComponentId, string> = {
    rmbg:        "onnx/export_rmbg.py",
    dinov3:      "onnx/export_dinov3.py",
    vae_encoder: "onnx/export_vae_encoder.py",
    flow:        "onnx/export_flow_model.py",
    octree_step: "onnx/export_octree_decoder.py",
    gs_decoder:  "onnx/export_gs_decoder.py",
  };
  const rerunCmds = missingComponents.map(id => `  python ${exportScript[id]}`);
  banner.innerHTML = `
    <strong>Missing model files for: ${names.join(", ")}.</strong>
    The browser pipeline can't run until these are present in <code>onnx/out/</code>.
    On the export host:
    <pre>${rerunCmds.join("\n")}
  python onnx/quantize.py --variants fp16,int8,int4</pre>
    Then refresh this page.
  `;
  const app = $("#app")!;
  const configSection = document.querySelector("section.config");
  app.insertBefore(banner, configSection ?? null);
  // disable Generate while anything is missing
  const runBtn = $("#run-btn") as HTMLButtonElement;
  runBtn.disabled = true;
  runBtn.title = `missing files for ${names.join(", ")}`;
}

function updateTotal() {
  let total = 0;
  for (const c of COMPONENTS) {
    const v = selection[c.id as ComponentId];
    total += variantSize[`${c.id}/${v}`] ?? 0;
  }
  ($("#total-bar") as HTMLElement).textContent =
    total > 0 ? `total download: ${total.toFixed(0)} MB` : "total download: --";
}

let pipeline: Pipeline | null = null;
let preparedFile: File | null = null;
/** The most recent splat -- populated by Generate, by the stub button, or by
 *  the "Load .splat" file picker. Save button reads from here. Lets us
 *  iterate on the viewer without re-running the whole pipeline. */
let lastSplat: ArrayBuffer | null = null;
let lastSplatName = "splat.splat";

function setLastSplat(buf: ArrayBuffer, name: string) {
  lastSplat = buf;
  lastSplatName = name;
  ($("#save-splat-btn") as HTMLButtonElement).disabled = false;
}

$("#image-input")!.addEventListener("change", (e) => {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (f) {
    preparedFile = f;
    // Keep Generate disabled if any component is missing -- image input alone
    // shouldn't override the missing-files block.
    if (missingComponents.length === 0) {
      ($("#run-btn") as HTMLButtonElement).disabled = false;
    }
    ($("#status") as HTMLElement).textContent = `loaded ${f.name} (${(f.size/1e6).toFixed(1)} MB)`;
  }
});

$("#run-btn")!.addEventListener("click", async () => {
  if (!preparedFile) return;
  ($("#run-btn") as HTMLButtonElement).disabled = true;
  const spinner = $("#viewer-spinner") as HTMLElement;
  const spinnerMsg = $("#spinner-msg") as HTMLElement;
  spinner.hidden = false;
  spinnerMsg.textContent = "preprocessing...";
  log("preprocessing image...");

  try {
    let prep;
    if (rawPrep) {
      log("raw mode: skipping RMBG/crop/composite, using uploaded image as-is");
      prep = await preprocessImageRaw(preparedFile);
    } else {
      let rmbg = null;
      if (selection.rmbg !== undefined) {
        try { rmbg = await loadComponent("rmbg", selection.rmbg); }
        catch (e) { log(`rmbg load failed: ${e}`); }
      }
      prep = await preprocessImage(preparedFile, rmbg, {
        erodeRadius:   prepCfg.erodeRadius,
        cropMargin:    prepCfg.cropMargin,
        bboxThreshold: prepCfg.bboxThreshold,
      });
    }
    log(`prep done -- preview ${prep.previewBlob.size} B`);
    // Show the post-RMBG image in the top-right of the viewer so it's
    // obvious what the encoders are actually seeing.
    const prepImg = $("#prep-img") as HTMLImageElement;
    if (prepImg.src.startsWith("blob:")) URL.revokeObjectURL(prepImg.src);
    prepImg.src = URL.createObjectURL(prep.previewBlob);
    prepImg.hidden = false;
    // Auto-download the preprocessed image whenever DEBUG flag is set, so we
    // can visually check that the encoders are seeing what we think they are.
    if ((window as any).__debug_prep) {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(prep.previewBlob);
      a.download = `prep-${preparedFile.name.replace(/\.[^.]+$/, "")}.png`;
      a.click(); URL.revokeObjectURL(a.href);
    }

    pipeline ??= new Pipeline();
    let refNoise: { latent: Float32Array; camera: Float32Array } | undefined;
    let refFlowLatent: Float32Array | undefined;
    if (refBase) {
      try {
        refNoise = await loadRefNoise(refBase);
        log(`refNoise loaded: latent[${refNoise.latent.length}] camera[${refNoise.camera.length}]`);
      } catch (e) {
        log(`refNoise load failed (${e}); falling back to JS randn`);
      }
      if (skipFlow) {
        try {
          refFlowLatent = await loadRefBin(`${refBase.replace(/\/$/, "")}/flow_latent.bin`);
          log(`refFlowLatent loaded: ${refFlowLatent.length} floats`);
        } catch (e) {
          log(`refFlowLatent load failed (${e}); flow loop will run normally`);
        }
      }
    }
    const viewerHost = $("#splat-canvas")!.parentElement!;
    const splat = await pipeline.run(prep.rgbMinus1to1, prep.rgbImagenet, {
      refNoise, refFlowLatent,
      opacityBoost,
      steps: sampling.steps,
      guidanceScale: sampling.guidanceScale,
      shift: sampling.shift,
      numGaussians: sampling.numGaussians,
      selection,
      flowFlash: kernels.flowFlash,
      flowGpuIo: kernels.flowGpuIo,
      vanilla: kernels.vanilla,
      onStatus: (s: string) => {
        ($("#status") as HTMLElement).textContent = s;
        spinnerMsg.textContent = s;
        log(s);
      },
      onProgress: (f: number) => { ($("#progress") as HTMLProgressElement).value = f; },
    });
    log(`got splat: ${splat.byteLength} bytes`);
    const stem = preparedFile.name.replace(/\.[^.]+$/, "") || "splat";
    setLastSplat(splat, `${stem}.splat`);
    await setSplat(viewerHost, splat);
    log(`splat mounted in viewer`);
  } catch (e) {
    log(`ERROR: ${(e as Error).stack ?? e}`);
  } finally {
    ($("#run-btn") as HTMLButtonElement).disabled = false;
    ($("#viewer-spinner") as HTMLElement).hidden = true;
  }
});

$("#save-splat-btn")!.addEventListener("click", () => {
  if (!lastSplat) return;
  const url = URL.createObjectURL(new Blob([lastSplat], { type: "application/octet-stream" }));
  const a = document.createElement("a");
  a.href = url; a.download = lastSplatName;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  log(`saved ${lastSplatName} (${lastSplat.byteLength} bytes)`);
});

$("#splat-input")!.addEventListener("change", async (e) => {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (!f) return;
  log(`loading ${f.name} (${f.size} bytes)`);
  const buf = await f.arrayBuffer();
  setLastSplat(buf, f.name);
  const viewerHost = $("#splat-canvas")!.parentElement!;
  await setSplat(viewerHost, buf);
  log(`splat mounted in viewer`);
});

renderSliders();
renderPrepSliders();
renderConfig();

// Kick off the model downloads in strict pipeline order. Each fetch lands in
// the shared model-cache.ts map; if the user clicks Generate before any of
// them finish, the corresponding stage's `take(url)` joins the in-flight
// Promise (same bytes, no second fetch).
log("warming HTTP cache for ONNX + wasm...");
prefetchModelFiles(defaultVariants())
  .then(() => log("prefetch done"))
  .catch(e => log(`prefetch failed (loadComponent will refetch on Generate): ${e}`));

/** Strict pipeline order. preprocess uses rmbg, encode uses dinov3 then
 *  vae_encoder, sampleLatent uses flow, decode uses octree_step then
 *  gs_decoder. Downloading in this exact order means take(url) for each
 *  stage always joins the currently-in-flight (or just-finished) download
 *  for that model -- never blocks on a model the inference doesn't need
 *  yet. */
const PREFETCH_IDS: ComponentId[] = [
  "rmbg", "dinov3", "vae_encoder", "flow", "octree_step", "gs_decoder",
];

/** Read defaults out of COMPONENTS, falling back to first-declared variant.
 *  Sync, doesn't wait for renderConfig's HEAD probes -- so we can start
 *  warming the cache the moment the page loads. */
function defaultVariants(): Record<string, Variant> {
  const out: Record<string, Variant> = {};
  for (const c of COMPONENTS) {
    const pick = c.variants.includes(c.default) ? c.default : c.variants[0];
    out[c.id] = pick;
  }
  return out;
}

async function prefetchModelFiles(variants: Record<string, Variant>) {
  // ORT-Web's JSEP worker assets first -- they need to be cached before any
  // session.create can succeed.
  const wasmBase = (import.meta as any).env?.VITE_WASM_BASE_URL ?? "/wasm/";
  const wb = wasmBase.endsWith("/") ? wasmBase : wasmBase + "/";
  await prefetchOne(wb + "ort-wasm-simd-threaded.jsep.wasm").catch(() => {});
  await prefetchOne(wb + "ort-wasm-simd-threaded.jsep.mjs").catch(() => {});

  const flash = kernels.flowFlash;
  for (const id of PREFETCH_IDS) {
    const v = variants[id];
    if (!v) continue;
    const url = modelUrl(id, v, id === "flow" && flash ? { flash: true } : undefined);
    log(`  prefetch ${id} (${v})...`);
    await prefetchOne(url).catch(() => {});
    await prefetchOne(url + ".data").catch(() => {});
  }
}

async function prefetchOne(url: string): Promise<void> {
  // Routes through the shared model cache. The Promise is stashed so
  // loadComponent's `take(url)` reuses it instead of issuing a duplicate
  // fetch -- the source of the same-page-load double-download we saw in
  // the Network panel.
  await prefetch(url);
}

const flashToggle = $("#flow-flash") as HTMLInputElement;
flashToggle.checked = kernels.flowFlash;
flashToggle.addEventListener("change", () => {
  kernels.flowFlash = flashToggle.checked;
  log(`flash attention -> ${kernels.flowFlash ? "on" : "off"} (next Generate reloads flow)`);
});

const gpuIoToggle = $("#flow-gpuio") as HTMLInputElement;
gpuIoToggle.checked = kernels.flowGpuIo;
gpuIoToggle.addEventListener("change", () => {
  kernels.flowGpuIo = gpuIoToggle.checked;
  log(`GPU IO binding -> ${kernels.flowGpuIo ? "on" : "off (forces CPU-tensor flow loop)"}`);
});

/** Fetch a flat fp32 LE `.bin` saved by python_debug.py. */
async function loadRefBin(url: string): Promise<Float32Array> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return new Float32Array(await r.arrayBuffer());
}

/** Fetch a `<base>/noise_latent.bin` + `<base>/noise_camera.bin` pair saved
 *  by python_debug.py. Both are flat fp32 little-endian. */
async function loadRefNoise(base: string): Promise<{ latent: Float32Array; camera: Float32Array }> {
  const root = base.replace(/\/$/, "");
  const [latent, camera] = await Promise.all([
    loadRefBin(`${root}/noise_latent.bin`),
    loadRefBin(`${root}/noise_camera.bin`),
  ]);
  return { latent, camera };
}

if (kernels.vanilla) {
  log("vanilla mode: serial encode + serial CFG + CPU flow loop + chunked flow");
  flashToggle.checked = false; flashToggle.disabled = true;
  gpuIoToggle.checked = false; gpuIoToggle.disabled = true;
}

log("ready. load an image and click Generate.");
